/**
 * "Needs a look" — the stuck-order cards between the pipeline tiles and the
 * order history. Left to right (Jason, 2026-09-20), each clock starting at the
 * step that precedes it rather than at checkout:
 *
 *   Store hasn't accepted     > 5 min  since the order arrived, nobody accepted on the tablet
 *   Unpacked orders           > 5 min  since accepted on the tablet, not yet marked ready
 *   Courier unassigned        > 10 min since marked ready, no courier allocated
 *   Order not collected       > 10 min since the courier was allocated, not collected
 *   Delivery not completed    > 20 min since collection, not delivered
 *   Completed after 1 hour    today's deliveries that took over an hour end to end
 *
 * The buckets are built server-side over the WHOLE pipeline (they ride along
 * with every orders fetch, like the "Right now" stats — see
 * order-analytics.buildNeedsALook), so they don't depend on which page of the
 * history is loaded or on the search/status filter. The last card is a daily
 * count and resets with "Delivered today".
 *
 * A card with nothing behind it still renders (count 0, muted) so the row
 * keeps its shape — the point is that the cards are always in the same place.
 */

import React, { useMemo, useSyncExternalStore } from 'react';
import { Box } from '@mui/material';
import { useAdminTheme } from '../../../admin-theme';
import { FONT_DATA, FONT_DISPLAY } from '../../../theme/grounds';
import { Band } from '../../reskin/primitives';
import {
  getNeedsALook,
  getOrderStats,
  subscribeOrderStats,
  type NeedsALook as NeedsALookBuckets,
  type NeedsALookKey,
  type OrderKPIStats,
} from '../orderAnalyticsStore';

interface Card {
  key: string;
  title: string;
  /** The mono sub-line — "oldest 24 min · Rosebank". */
  note: string;
  count: number;
  /** ember = act now, warn = watch, info = a tally, not an alarm. */
  tone: 'attn' | 'warned' | 'info';
  orderIds: string[];
}

const STUCK: Array<{ key: NeedsALookKey; title: string; minutes: number; tone: Card['tone']; empty: string }> = [
  { key: 'storeNotAccepted', title: 'Store hasn’t accepted', minutes: 5, tone: 'warned', empty: 'every order accepted' },
  { key: 'unpacked', title: 'Unpacked orders', minutes: 5, tone: 'attn', empty: 'nothing waiting' },
  { key: 'courierUnassigned', title: 'Courier unassigned', minutes: 10, tone: 'warned', empty: 'every ready order has a courier' },
  { key: 'notCollected', title: 'Order not collected', minutes: 10, tone: 'warned', empty: 'every courier has collected' },
  { key: 'deliveryNotCompleted', title: 'Delivery not completed', minutes: 20, tone: 'attn', empty: 'every trip on time' },
];

function buildCards(buckets: NeedsALookBuckets | null, stats: OrderKPIStats): Card[] {

  const cards: Card[] = STUCK.map((def) => {
    const bucket = buckets?.[def.key];
    const count = bucket?.count ?? 0;
    const note = count && bucket
      ? `oldest ${bucket.oldestMinutes ?? '?'} min${bucket.oldestStore ? ` · ${bucket.oldestStore}` : ''}`
      : def.empty;
    return {
      key: def.key,
      title: `${def.title} (over ${def.minutes} minutes)`,
      note,
      count,
      tone: def.tone,
      orderIds: bucket?.orderIds ?? [],
    };
  });

  cards.push({
    key: 'completedOverHour',
    title: 'Orders completed after 1 hour',
    note: 'today · resets at midnight',
    count: stats.completedOverHourToday ?? 0,
    tone: 'info',
    orderIds: [],
  });

  return cards;
}

interface NeedsALookProps {
  /** Flash the affected rows in the table, the way the insights panel does. */
  onHighlight?: (orderIds: string[]) => void;
}

export default function NeedsALook({ onHighlight }: NeedsALookProps) {
  const { colors } = useAdminTheme();
  // Both the buckets and the daily count come off the orders fetch; the store
  // holds them as stable references, so the snapshots are safe to subscribe to.
  const buckets = useSyncExternalStore(subscribeOrderStats, getNeedsALook, getNeedsALook);
  const stats = useSyncExternalStore(subscribeOrderStats, getOrderStats, getOrderStats);
  const cards = useMemo(() => buildCards(buckets, stats), [buckets, stats]);

  return (
    <>
      <Band note="orders the board thinks are stuck">Needs a look</Band>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(196px, 1fr))',
          gap: '10px',
          mb: '13px',
        }}
      >
        {cards.map((card) => {
          const live = card.count > 0;
          const clickable = live && !!onHighlight && card.orderIds.length > 0;
          const accent =
            card.tone === 'attn' ? 'var(--ember)' : card.tone === 'warned' ? colors.warning : colors.textPrimary;
          const line =
            card.tone === 'attn' ? 'var(--ember-line)' : card.tone === 'warned' ? 'var(--warn-line)' : colors.border;
          return (
            <Box
              key={card.key}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => onHighlight!(card.orderIds) : undefined}
              sx={{
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                p: '10px 13px',
                bgcolor: colors.bgCard,
                border: `1px solid ${live ? line : colors.border}`,
                borderRadius: '8px',
                cursor: clickable ? 'pointer' : 'default',
                '&:hover': clickable ? { bgcolor: 'var(--hover)' } : undefined,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: '0.84rem', fontWeight: 650, color: colors.textPrimary }}>{card.title}</Box>
                <Box sx={{ fontFamily: FONT_DATA, fontSize: '0.69rem', color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {card.note}
                </Box>
              </Box>
              <Box
                sx={{
                  fontFamily: FONT_DISPLAY, fontStretch: '125%', fontWeight: 800, fontSize: '1.4rem',
                  lineHeight: 1, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums',
                  color: live ? accent : colors.textMuted,
                }}
              >
                {card.count}
              </Box>
            </Box>
          );
        })}
      </Box>
    </>
  );
}
